import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@/lib/hooks/useToast'
import { useIndexedFoldersStore } from '@/lib/stores/indexedFoldersStore'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { type IndexedFolderConfig } from '@/lib/api/indexedFoldersApi'
import { MoreHorizontal, Trash2, Edit, Power, PowerOff } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

const PAGE_SIZE = 10

interface FolderConfigTableProps {
  folders: IndexedFolderConfig[]
  onEditFolder: (folder: IndexedFolderConfig) => void
  isLoading?: boolean
}

export function FolderConfigTable({
  folders,
  onEditFolder,
  isLoading = false,
}: FolderConfigTableProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { deleteFolder, toggleFolder } = useIndexedFoldersStore()

  const [folderToDelete, setFolderToDelete] = useState<IndexedFolderConfig | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isTogglingId, setIsTogglingId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const sortedFolders = useMemo(
    () => [...(folders ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [folders]
  )

  const totalPages = Math.max(1, Math.ceil(sortedFolders.length / PAGE_SIZE))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const paginatedFolders = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE
    return sortedFolders.slice(startIndex, startIndex + PAGE_SIZE)
  }, [sortedFolders, currentPage])

  const pageStart = sortedFolders.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const pageEnd = Math.min(currentPage * PAGE_SIZE, sortedFolders.length)

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-10 rounded-md bg-muted animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (sortedFolders.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center min-h-75">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-muted-foreground mb-2">
              {t('admin.indexedFolders.noFolders')}
            </h3>
            <p className="text-sm text-muted-foreground">{t('admin.indexedFolders.noFoldersDescription')}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const handleDelete = async () => {
    if (!folderToDelete) return
    setIsDeleting(true)
    try {
      await deleteFolder(folderToDelete.id)
      toast({
        title: t('admin.indexedFolders.success'),
        description: t('admin.indexedFolders.folderDeleted'),
      })
      setFolderToDelete(null)
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleToggle = async (folder: IndexedFolderConfig) => {
    setIsTogglingId(folder.id)
    try {
      await toggleFolder(folder.id, !folder.isActive)
      toast({
        title: t('admin.indexedFolders.success'),
        description: folder.isActive
          ? t('admin.indexedFolders.folderDeactivated')
          : t('admin.indexedFolders.folderActivated'),
      })
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
        variant: 'destructive',
      })
    } finally {
      setIsTogglingId(null)
    }
  }

  const truncatePath = (path: string | null | undefined, maxLength = 44) => {
    if (!path || typeof path !== 'string') return '—'
    if (path.length <= maxLength) return path
    return '...' + path.slice(-(maxLength - 3))
  }

  const getFolderPath = (folder: IndexedFolderConfig): string => {
    return typeof folder?.folderPath === 'string' ? folder.folderPath : ''
  }

  return (
    <TooltipProvider>
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">{t('admin.indexedFolders.name')}</TableHead>
                <TableHead scope="col">{t('admin.indexedFolders.folderPath')}</TableHead>
                <TableHead scope="col">{t('admin.indexedFolders.displayOrder')}</TableHead>
                <TableHead scope="col">{t('admin.indexedFolders.active')}</TableHead>
                <TableHead scope="col" className="text-right">{t('admin.indexedFolders.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedFolders.map(folder => {
                const folderPath = getFolderPath(folder)
                const truncatedPath = truncatePath(folderPath)
                const isPathTruncated = truncatedPath !== folderPath && folderPath.length > 0

                return (
                <TableRow
                  key={folder.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => onEditFolder(folder)}
                >
                  <TableCell className="font-medium">{folder.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-0 w-[40%]">
                    {isPathTruncated ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate block">{truncatedPath}</span>
                        </TooltipTrigger>
                        <TooltipContent>{folderPath}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="truncate block">{truncatedPath}</span>
                    )}
                  </TableCell>
                  <TableCell>{folder.displayOrder}</TableCell>
                  <TableCell>
                    <Badge
                      variant={folder.isActive ? 'default' : 'secondary'}
                      className={folder.isActive ? 'bg-green-600 hover:bg-green-600 text-white' : ''}
                    >
                      {folder.isActive ? t('admin.indexedFolders.active') : t('admin.indexedFolders.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="link"
                          className="h-11 w-11 p-0"
                          onClick={event => event.stopPropagation()}
                          size="sm"
                        >
                          <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">{t('common.actions')}</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className='bg-white'>
                        <DropdownMenuItem
                          onClick={event => {
                            event.stopPropagation()
                            onEditFolder(folder)
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          {t('admin.indexedFolders.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={event => {
                            event.stopPropagation()
                            void handleToggle(folder)
                          }}
                          disabled={isTogglingId === folder.id || isDeleting}
                        >
                          {folder.isActive ? (
                            <>
                              <PowerOff className="mr-2 h-4 w-4" />
                              {t('admin.indexedFolders.deactivate')}
                            </>
                          ) : (
                            <>
                              <Power className="mr-2 h-4 w-4" />
                              {t('admin.indexedFolders.activate')}
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={event => {
                            event.stopPropagation()
                            setFolderToDelete(folder)
                          }}
                          className="text-destructive focus:text-destructive"
                          disabled={isDeleting}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('admin.indexedFolders.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <CardContent className="border-t pt-4 pb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {t('admin.users.showing')} {pageStart}-{pageEnd} {t('admin.users.of')} {sortedFolders.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="min-h-[44px] min-w-[44px]"
                onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
                disabled={currentPage === 1}
              >
                {t('admin.users.previous')}
              </Button>
              <Button
                variant="outline"
                className="min-h-[44px] min-w-[44px]"
                onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
                disabled={currentPage >= totalPages}
              >
                {t('admin.users.next')}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!folderToDelete} onOpenChange={open => !open && setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.indexedFolders.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.indexedFolders.deleteConfirmDescription', {
                name: folderToDelete?.name || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('common.deleting') : t('admin.indexedFolders.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  )
}
